import { useEffect, useId, useRef, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/i18n/use-t';
import { ArrowLeftIcon, CloseIcon, ImageIcon } from './icons';
import { Button, Card, IconButton, cx } from './ui';

/**
 * The page kit: one shape for every screen.
 *
 * The reference dashboards all agree on it, and an operator who learns one
 * screen should already know the next: a header with the title, what it is
 * for, and the page's own actions on the far side; tabs under it when the
 * page has views; then the body, which runs to the bottom of the window.
 *
 * "To the bottom" is the point of `fill`: a list page is exactly the height of
 * the window on a desktop, its card stretches to the bottom edge however few
 * rows it holds, and a long table scrolls inside the card with its header
 * pinned — so the search box and the column names never scroll away. On a
 * phone the page simply scrolls, which is what a thumb expects.
 */

type Width = 'full' | 'wide' | 'narrow';

const WIDTHS: Record<Width, string> = {
  full: '',
  wide: 'mx-auto w-full max-w-[88rem]',
  narrow: 'mx-auto w-full max-w-5xl',
};

export function Page({
  title,
  subtitle,
  back,
  meta,
  actions,
  tabs,
  width = 'full',
  fill = false,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Where "back" goes — a detail page's list. `wideHidden` drops it where a side menu already offers the way back. */
  back?: { to: string; label: string; wideHidden?: boolean };
  /** Beside the title: a status pill, an identifier. */
  meta?: ReactNode;
  /** The page's own buttons, primary last so it sits at the far edge. */
  actions?: ReactNode;
  /** Views of the page, drawn under the header. */
  tabs?: ReactNode;
  width?: Width;
  /** Exactly the window's height on a desktop, for a list whose table scrolls inside its card. */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cx('flex min-h-full flex-col', fill && 'lg:h-full')}>
      <div className={cx('flex flex-1 flex-col', fill && 'lg:min-h-0', WIDTHS[width])}>
        <header className="px-4 pt-6 pb-5 lg:px-8 lg:pt-8">
          {back ? (
            <Link
              to={back.to}
              className={cx(
                'mb-3 inline-flex items-center gap-1.5 rounded-control text-[13px] font-medium text-ink-muted transition-colors hover:text-brand-700',
                back.wideHidden && 'xl:hidden',
              )}
            >
              <ArrowLeftIcon className="size-4 rtl:-scale-x-100" />
              {back.label}
            </Link>
          ) : null}
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="text-[1.375rem] leading-tight font-bold tracking-tight text-ink lg:text-[1.625rem]">{title}</h1>
                {meta}
              </div>
              {subtitle ? <p className="mt-1.5 max-w-3xl text-sm text-ink-muted">{subtitle}</p> : null}
            </div>
            {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
          </div>
          {tabs ? <div className="mt-5">{tabs}</div> : null}
        </header>

        <div className={cx('flex flex-1 flex-col gap-5 px-4 pb-6 lg:px-8 lg:pb-8', fill && 'lg:min-h-0')}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

export interface TabItem<T extends string> {
  id: T;
  label: string;
  /** A count beside the label — "Pending 4". */
  count?: number | string;
  /** Shown, but muted: a tab that needs something first. */
  muted?: boolean;
}

/**
 * Views of one thing, underlined — the header's tabs and a card's.
 *
 * Scrolls sideways rather than wrapping: a second row of tabs reads as a
 * second set of choices.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** What the tabs choose between, for a screen reader. */
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx('flex gap-1.5 overflow-x-auto border-b border-line [scrollbar-width:none]', className)}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={cx(
              '-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm whitespace-nowrap transition-colors',
              active
                ? 'border-brand-500 font-semibold text-ink'
                : 'border-transparent font-medium text-ink-muted hover:border-line-strong hover:text-ink',
              item.muted && !active && 'opacity-60',
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span
                data-numeric
                className={cx(
                  'rounded-full px-1.5 py-px text-[11px] leading-4 font-semibold',
                  active ? 'bg-brand-500/15 text-brand-700' : 'bg-ink/[0.06] text-ink-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** A small either/or inside a card — "Grid | List", "Arabic | English". */
export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  label?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-control border border-line bg-ink/[0.04] p-0.5">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="radio"
          aria-checked={item.id === value}
          onClick={() => onChange(item.id)}
          className={cx(
            'rounded-[calc(var(--radius-control)-3px)] px-3 py-1.5 text-xs font-medium transition-colors',
            item.id === value ? 'bg-surface font-semibold text-ink shadow-card ring-1 ring-line' : 'text-ink-muted hover:text-ink',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Lists ───────────────────────────────────────────────────────────────────

/**
 * The card a list lives in. On a desktop it takes the rest of the page's
 * height, so the page reads as one surface down to the bottom edge.
 */
export function ListCard({ children, className }: { children: ReactNode; className?: string }) {
  return <Card className={cx('flex flex-1 flex-col lg:min-h-0', className)}>{children}</Card>;
}

/** The strip at the top of a list card: search, filters, view tabs. */
export function ListToolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('flex flex-col gap-3 border-b border-line p-4', className)}>{children}</div>;
}

/** Where the rows scroll, inside the card, under a pinned header. */
export function TableScroll({ children, className }: { children: ReactNode; className?: string }) {
  // A shade on whichever edge has more columns — a phone sees there is more to the right.
  return <div className={cx('scroll-shadows min-h-0 overflow-auto lg:flex-1', className)}>{children}</div>;
}

/** A table with the house spacing. Pair with `Th`/`Td` so every list reads alike. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return <table className={cx('w-full border-separate border-spacing-0 text-sm', className)}>{children}</table>;
}

export function Th({
  children,
  align = 'start',
  className,
  ...props
}: Omit<ComponentProps<'th'>, 'align'> & { align?: 'start' | 'end' | 'center' }) {
  return (
    <th
      scope="col"
      className={cx(
        // Sticky inside TableScroll: the column names stay while the rows move.
        // A Mist band, so the header reads as the table's head and not a row.
        'sticky top-0 z-10 border-b border-line bg-surface-sunken px-3 py-2.5 text-xs font-semibold whitespace-nowrap text-ink-muted first:ps-4 last:pe-4',
        align === 'end' ? 'text-end' : align === 'center' ? 'text-center' : 'text-start',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'start',
  className,
  ...props
}: Omit<ComponentProps<'td'>, 'align'> & { align?: 'start' | 'end' | 'center' }) {
  return (
    <td
      className={cx(
        'border-b border-line px-3 py-3.5 align-middle first:ps-4 last:pe-4',
        align === 'end' ? 'text-end' : align === 'center' ? 'text-center' : 'text-start',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

/**
 * A row that lights up under the pointer; `onClick` makes the whole row the
 * target, and `selected` keeps it lit — a ticked row in a bulk selection.
 */
export function Tr({
  children,
  className,
  onClick,
  selected = false,
  ...props
}: ComponentProps<'tr'> & { selected?: boolean }) {
  return (
    <tr
      aria-selected={selected || undefined}
      className={cx(
        'transition-colors',
        selected ? 'bg-brand-50 hover:bg-brand-50' : 'hover:bg-brand-500/[0.045]',
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </tr>
  );
}

/** "Load more" at the foot of a list — a cursor walked one page at a time. */
export function LoadMore({
  visible,
  loading,
  onClick,
}: {
  visible: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  const t = useT();
  if (!visible) return null;

  return (
    <div className="flex justify-center border-t border-line p-3">
      <Button variant="secondary" onClick={onClick} disabled={loading}>
        {loading ? t('loading') : t('loadMore')}
      </Button>
    </div>
  );
}

// ─── Detail pages ────────────────────────────────────────────────────────────

/**
 * The resource page: the thing itself in a wide column, and what describes it
 * — status, who, where it is filed — in a narrow one beside it. On a phone the
 * side column comes first, because status is what someone opens it to check.
 */
export function DetailLayout({
  main,
  aside,
  asideFirstOnMobile = false,
  stickyAside = false,
}: {
  main: ReactNode;
  aside: ReactNode;
  asideFirstOnMobile?: boolean;
  /** Keep the aside in view while the main column scrolls — a summary with the page's one button. */
  stickyAside?: boolean;
}) {
  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_21rem]">
      <div className={cx('flex min-w-0 flex-col gap-5', asideFirstOnMobile && 'order-2 xl:order-1')}>{main}</div>
      <aside
        className={cx(
          'flex min-w-0 flex-col gap-5',
          asideFirstOnMobile && 'order-1 xl:order-2',
          stickyAside && 'xl:sticky xl:top-5',
        )}
      >
        {aside}
      </aside>
    </div>
  );
}

/** A card with a titled head, the body below — the unit every form is made of. */
export function Section({
  title,
  subtitle,
  action,
  children,
  flush = false,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  /** The body runs edge to edge — for a table or a list of rows. */
  flush?: boolean;
  className?: string;
}) {
  return (
    <Card className={className}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-3.5">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-[13px] text-ink-muted">{subtitle}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      <div className={flush ? '' : 'px-5 pb-5'}>{children}</div>
    </Card>
  );
}

/**
 * A settings group: what it is and why, beside the fields that change it.
 * Read top to bottom on a phone, side by side on a desktop.
 */
export function AnnotatedSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-4 border-b border-line pb-8 last:border-0 last:pb-0 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-8">
      <div>
        <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
        {description ? <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{description}</p> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/** A label and its value, stacked — the side column's facts. */
export function KeyValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

/** A label and an amount on one line — a column of totals that adds up on screen. */
export function AmountRow({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={emphasis ? 'font-semibold text-ink' : 'text-ink-muted'}>{label}</dt>
      <dd data-numeric className={emphasis ? 'font-semibold text-ink' : 'text-ink'}>
        {value}
      </dd>
    </div>
  );
}

// ─── Media ───────────────────────────────────────────────────────────────────

const THUMBS = {
  xs: 'size-8',
  sm: 'size-10',
  md: 'size-12',
  lg: 'size-16',
} as const;

/** A picture in a list row, or the shape of one when there is none. */
export function Thumb({
  src,
  alt = '',
  size = 'md',
  portrait = false,
  swatch,
  className,
}: {
  src?: string | null;
  alt?: string;
  size?: keyof typeof THUMBS;
  /** Three wide by four tall — a look's model photograph. */
  portrait?: boolean;
  /** A colour to fill with when there is no picture. */
  swatch?: string | null;
  className?: string;
}) {
  const shape = cx(
    'shrink-0 overflow-hidden rounded-[calc(var(--radius-control)-2px)] border border-line bg-surface-sunken',
    portrait ? cx(THUMBS[size], 'aspect-[3/4] h-auto') : THUMBS[size],
    className,
  );

  if (src) return <img src={src} alt={alt} loading="lazy" className={cx(shape, 'object-cover')} />;
  if (swatch) return <span aria-hidden className={shape} style={{ backgroundColor: swatch }} />;
  return (
    <span aria-hidden className={cx(shape, 'inline-flex items-center justify-center text-ink-faint')}>
      <ImageIcon className="size-1/2" />
    </span>
  );
}

// ─── Overlays ────────────────────────────────────────────────────────────────

/**
 * A panel from the far edge, for creating or editing one thing without
 * leaving the list it belongs to — the list stays in view, and closing the
 * panel puts the operator back exactly where they were.
 */
export function Sheet({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
  size = 'md',
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const t = useT();
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // The first field, not the close button: the panel was opened to type in.
    const first = panel.current?.querySelector<HTMLElement>('input, textarea, select');
    (first ?? panel.current)?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="animate-fade absolute inset-0 bg-scrim/40 backdrop-blur-[3px]" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(
          // The whole screen on a phone; on anything wider, a floating panel
          // inset from the edge with the float radius — it reads as on top of
          // the page rather than part of it.
          'animate-sheet relative flex h-full w-full flex-col overflow-hidden bg-surface shadow-float outline-none sm:m-2 sm:h-[calc(100%-1rem)] sm:rounded-float sm:border sm:border-line',
          size === 'md' ? 'sm:max-w-lg' : size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-4xl',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 py-5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[17px] font-semibold text-ink">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p> : null}
          </div>
          <IconButton label={t('close')} onClick={onClose} className="size-9 shrink-0 border-0">
            <CloseIcon className="size-[18px]" />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-sunken px-6 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The bar a form saves from, pinned to the bottom of the window while the
 * form is longer than it. One place to save a long form, always in reach,
 * instead of a button at the end of a scroll nobody finishes.
 */
export function SaveBar({
  saving,
  dirty = true,
  message,
  form,
  onSave,
  onDiscard,
  saveLabel,
  children,
}: {
  saving: boolean;
  /** Whether there is anything to save; the bar says so when there is. */
  dirty?: boolean;
  /** An error or a note, at the start of the bar. */
  message?: ReactNode;
  /** The id of the form the save button submits. */
  form?: string;
  onSave?: () => void;
  onDiscard?: () => void;
  saveLabel?: string;
  /** Extra buttons, before discard. */
  children?: ReactNode;
}) {
  const t = useT();

  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-auto border-t border-line bg-surface/90 px-4 py-3 shadow-[0_-8px_24px_-12px_oklch(0.234_0.035_var(--brand-hue)/0.12)] backdrop-blur-md lg:-mx-8 lg:px-8">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="me-auto min-w-0 text-sm">
          {message ?? (dirty ? <span className="text-ink-muted">{t('unsavedChanges')}</span> : null)}
        </div>
        {children}
        {onDiscard ? (
          <Button variant="secondary" onClick={onDiscard} disabled={saving}>
            {t('discard')}
          </Button>
        ) : null}
        <Button type={form ? 'submit' : 'button'} form={form} onClick={onSave} disabled={saving || !dirty}>
          {saving ? t('saving') : (saveLabel ?? t('save'))}
        </Button>
      </div>
    </div>
  );
}
